/**
 * Teclado de texto del promotor — variante propia para componer el mensaje a
 * compartir. Reusa la ESTÉTICA del teclado del sistema (.pm-tec) pero con su
 * propio motor: escribe en la POSICIÓN DEL CURSOR (no append), soporta acentos
 * PT/ES (long-press), emojis, flechas para mover el cursor, y formato WhatsApp.
 *
 * NO toca el core de modal-formulario (que usan PIN/teléfono/login) — es aislado.
 *
 * MÁQUINA DE ESTADOS DE FOCO (el 90% del trabajo real de un teclado custom):
 *  - El textarea usa inputmode="none" → el SO NO abre su teclado nativo encima.
 *  - Cada tecla actúa en `pointerdown` con preventDefault, lo que (a) impide el
 *    blur del textarea y (b) NO depende del evento `click` sintético —que iOS
 *    Safari CANCELA cuando se hace preventDefault del pointerdown—. Ese era el
 *    bug "no dispara": el handler vivía en `click`, que en iOS nunca llegaba.
 *  - El caret (selectionStart/End) se preserva en cada escritura programática.
 */

// Acentos por tecla base (long-press abre el popover). Cubre PT + ES.
const ACENTOS = {
    a: ['á', 'à', 'ã', 'â', 'ä'],
    e: ['é', 'è', 'ê', 'ë'],
    i: ['í', 'ì', 'î', 'ï'],
    o: ['ó', 'ò', 'õ', 'ô', 'ö'],
    u: ['ú', 'ù', 'û', 'ü'],
    c: ['ç'],
    n: ['ñ'],
};

const FILAS = [
    ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
    ['⇧', 'z', 'x', 'c', 'v', 'b', 'n', 'm', '⌫'],
    ['123', '😀', 'espacio', 'undo', 'redo', '◀', '▶'],
];

// Capa numérica/símbolos (el botón 123 alterna).
const FILAS_NUM = [
    ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
    ['@', '#', '%', '&', '*', '(', ')', '-', '_'],
    ['!', '?', ',', '.', ':', ';', '/', '"', '⌫'],
    ['abc', '😀', 'espacio', 'undo', 'redo', '◀', '▶'],
];

const EMOJIS = [
    '💰',
    '💸',
    '🤑',
    '💵',
    '🪙',
    '💎',
    '👑',
    '🎰',
    '🎁',
    '🏆',
    '🔥',
    '⚡',
    '🚀',
    '✨',
    '💥',
    '⭐',
    '🌟',
    '🎯',
    '🌈',
    '💯',
    '😎',
    '🤩',
    '🥳',
    '😍',
    '🙌',
    '👏',
    '💪',
    '🎉',
    '👍',
    '😏',
    '👉',
    '👇',
    '➡️',
    '▶️',
    '⏰',
    '✅',
    '🆕',
    '🔝',
    '▸',
    '✦',
];

const MS_LONG_PRESS = 350;

/**
 * Foco SIN teclado nativo del SO. Es la pieza central. En iOS Safari NO existe la
 * VirtualKeyboard API: `inputmode="none"` por sí solo a veces no basta en versiones
 * viejas, por eso combinamos con el truco readOnly intermitente SOLO en iOS. En
 * Chromium alcanza con inputmode="none".
 *
 * NO usamos blur en ningún punto del ciclo de teclas → el caret real se conserva.
 * (La supresión del teclado nativo —VirtualKeyboard API en Chromium, readOnly en
 *  iOS— la gobierna el editor que monta este teclado; acá sólo re-enfocamos.)
 */
function enfocarSinTecladoNativo(textarea) {
    if (document.activeElement !== textarea) {
        textarea.focus({ preventScroll: true });
    }
}

/** Inserta texto en la posición del cursor del textarea, preservando selección. */
function escribirEnCursor(textarea, txt) {
    const ini = textarea.selectionStart;
    const fin = textarea.selectionEnd;
    const val = textarea.value;
    textarea.value = val.slice(0, ini) + txt + val.slice(fin);
    const pos = ini + txt.length;
    textarea.setSelectionRange(pos, pos);
}

function borrar(textarea) {
    const ini = textarea.selectionStart;
    const fin = textarea.selectionEnd;
    const val = textarea.value;
    if (ini !== fin) {
        // Hay selección: borrar la selección.
        textarea.value = val.slice(0, ini) + val.slice(fin);
        textarea.setSelectionRange(ini, ini);
    } else if (ini > 0) {
        // Sin selección: borrar el carácter anterior.
        textarea.value = val.slice(0, ini - 1) + val.slice(ini);
        textarea.setSelectionRange(ini - 1, ini - 1);
    }
}

function moverCursor(textarea, delta) {
    const pos = Math.max(0, Math.min(textarea.value.length, textarea.selectionStart + delta));
    textarea.setSelectionRange(pos, pos);
}

// Selección con shift+flecha: extiende (o reduce) la selección un carácter. El
// "ancla" es el extremo que NO se mueve; movemos el "foco" (el otro extremo).
function extenderSeleccion(textarea, delta, anclaRef) {
    const len = textarea.value.length;
    // El ancla se fija la primera vez que se empieza a seleccionar.
    if (anclaRef.valor == null) anclaRef.valor = textarea.selectionStart;
    const ancla = anclaRef.valor;
    // El foco actual es el extremo distinto del ancla.
    const focoActual = textarea.selectionEnd === ancla ? textarea.selectionStart : textarea.selectionEnd;
    const nuevoFoco = Math.max(0, Math.min(len, focoActual + delta));
    textarea.setSelectionRange(Math.min(ancla, nuevoFoco), Math.max(ancla, nuevoFoco));
}

// Selecciona la palabra bajo una posición (doble-tap). Una "palabra" = run de
// caracteres no-espacio. Devuelve true si seleccionó algo.
function seleccionarPalabra(textarea, pos) {
    const val = textarea.value;
    if (!val) return false;
    const esEspacio = c => c === ' ' || c === '\n' || c === '\t';
    let ini = Math.min(pos, val.length - 1);
    if (ini < 0) return false;
    if (esEspacio(val[ini])) return false; // tap en espacio: no selecciona
    while (ini > 0 && !esEspacio(val[ini - 1])) ini--;
    let fin = pos;
    while (fin < val.length && !esEspacio(val[fin])) fin++;
    if (fin <= ini) return false;
    textarea.setSelectionRange(ini, fin);
    return true;
}

// Historial de undo/redo (snapshots {value,start,end}). inputmode=none mata el
// Cmd+Z nativo, así que lo implementamos propio. Pila acotada (anti-memoria).
const MAX_HISTORIAL = 50;
function crearHistorial(textarea) {
    const pila = [{ value: textarea.value, start: textarea.selectionStart || 0, end: textarea.selectionEnd || 0 }];
    let cursor = 0; // índice del estado actual en la pila
    function snapshot() {
        const actual = pila[cursor];
        const val = textarea.value;
        // No duplicar si el valor no cambió (ej. solo movió el caret).
        if (actual && actual.value === val) {
            actual.start = textarea.selectionStart;
            actual.end = textarea.selectionEnd;
            return;
        }
        // Al escribir tras un undo, se descarta el "futuro" (rama redo).
        pila.length = cursor + 1;
        pila.push({ value: val, start: textarea.selectionStart, end: textarea.selectionEnd });
        if (pila.length > MAX_HISTORIAL) pila.shift();
        cursor = pila.length - 1;
    }
    function aplicar(estado) {
        textarea.value = estado.value;
        textarea.setSelectionRange(estado.start, estado.end);
    }
    return {
        snapshot,
        undo() {
            if (cursor <= 0) return false;
            cursor--;
            aplicar(pila[cursor]);
            return true;
        },
        redo() {
            if (cursor >= pila.length - 1) return false;
            cursor++;
            aplicar(pila[cursor]);
            return true;
        },
        puedeUndo: () => cursor > 0,
        puedeRedo: () => cursor < pila.length - 1,
    };
}

/**
 * Crea el teclado del promotor. Devuelve { el, destroy }.
 * @param {HTMLTextAreaElement} textarea
 * @param {()=>void} onChange
 */
export function crearTecladoPromotor(textarea, onChange) {
    const el = document.createElement('div');
    el.className = 'pm-teclado';
    let shift = false;
    let capaNum = false;
    let popoverAcentos = null;
    let panelEmoji = null;
    const historial = crearHistorial(textarea); // undo/redo (inputmode=none mata Cmd+Z)
    const anclaSeleccion = { valor: null }; // ancla para shift+flecha (selección)
    // Caret guardado: se persiste ANTES de cualquier operación que pueda perder
    // foco, y se restaura al re-enfocar. iOS suelta el foco solo (scroll/rotación);
    // sin esto el caret volvería a 0.
    let caretGuardado = { start: textarea.value.length, end: textarea.value.length };

    function guardarCaret() {
        if (typeof textarea.selectionStart === 'number') {
            caretGuardado = { start: textarea.selectionStart, end: textarea.selectionEnd };
        }
    }
    function restaurarCaret() {
        enfocarSinTecladoNativo(textarea);
        try {
            textarea.setSelectionRange(caretGuardado.start, caretGuardado.end);
        } catch (_) {
            /* setSelectionRange falla si el textarea está detached: ignorar */
        }
    }

    function cambio() {
        historial.snapshot(); // cada acción que muta el texto se guarda en el historial
        guardarCaret();
        if (onChange) onChange();
        actualizarBotonesUndo();
    }
    // Habilita/deshabilita ↶↷ según el historial.
    function actualizarBotonesUndo() {
        const u = el.querySelector('[data-k="undo"]');
        const re = el.querySelector('[data-k="redo"]');
        if (u) u.classList.toggle('pm-tec--off', !historial.puedeUndo());
        if (re) re.classList.toggle('pm-tec--off', !historial.puedeRedo());
    }

    function render() {
        const filas = capaNum ? FILAS_NUM : FILAS;
        el.innerHTML = filas
            .map(fila => `<div class="pm-tec-fila">${fila.map(k => construirTecla(k)).join('')}</div>`)
            .join('');
        // El panel de emojis vive fuera del re-render de filas; si estaba abierto,
        // re-insertarlo arriba.
        if (panelEmoji) el.insertBefore(panelEmoji, el.firstChild);
    }

    function construirTecla(k) {
        if (k === 'espacio')
            return `<button type="button" class="pm-tec pm-tec--espacio" data-k="espacio">espaço</button>`;
        if (k === '⇧')
            return `<button type="button" class="pm-tec pm-tec--esp ${shift ? 'pm-tec--on' : ''}" data-k="⇧">⇧</button>`;
        if (k === '⌫') return `<button type="button" class="pm-tec pm-tec--esp" data-k="⌫">⌫</button>`;
        if (k === '123' || k === 'abc')
            return `<button type="button" class="pm-tec pm-tec--esp" data-k="capa">${k}</button>`;
        if (k === '😀') return `<button type="button" class="pm-tec pm-tec--esp" data-k="emoji">😀</button>`;
        if (k === 'undo') return `<button type="button" class="pm-tec pm-tec--esp" data-k="undo" title="deshacer">${iconUndo()}</button>`;
        if (k === 'redo') return `<button type="button" class="pm-tec pm-tec--esp" data-k="redo" title="rehacer">${iconRedo()}</button>`;
        if (k === '◀') return `<button type="button" class="pm-tec pm-tec--esp" data-k="◀">◀</button>`;
        if (k === '▶') return `<button type="button" class="pm-tec pm-tec--esp" data-k="▶">▶</button>`;
        const car = shift ? k.toUpperCase() : k;
        const tieneAcentos = ACENTOS[k] ? ' data-acentos="1"' : '';
        return `<button type="button" class="pm-tec" data-k="${k}"${tieneAcentos}>${car}</button>`;
    }

    function cerrarAcentos() {
        if (popoverAcentos) {
            popoverAcentos.remove();
            popoverAcentos = null;
        }
    }

    // ── Ejecutor de teclas. Recibe el botón [data-k] o [data-acento] que se tocó.
    //    Se llama desde pointerup/click (lo que llegue primero), con guard de gesto
    //    para no ejecutar dos veces.
    function ejecutarTecla(btn) {
        if (!btn) return;
        // Acento del popover.
        if (btn.dataset.acento != null) {
            escribirEnCursor(textarea, btn.dataset.acento);
            cerrarAcentos();
            if (shift) {
                shift = false;
                render();
            }
            cambio();
            return;
        }
        const k = btn.dataset.k;
        if (k == null) return;
        if (k === '⇧') {
            shift = !shift;
            render();
            return;
        }
        if (k === 'capa') {
            capaNum = !capaNum;
            render();
            return;
        }
        if (k === 'emoji') {
            toggleEmojis();
            return;
        }
        if (k === '⌫') {
            borrar(textarea);
            cambio();
            return;
        }
        if (k === 'undo') {
            if (historial.undo()) {
                guardarCaret();
                if (onChange) onChange();
                actualizarBotonesUndo();
            }
            return;
        }
        if (k === 'redo') {
            if (historial.redo()) {
                guardarCaret();
                if (onChange) onChange();
                actualizarBotonesUndo();
            }
            return;
        }
        // Flechas: con SHIFT activo → SELECCIONAN (extienden el rango); sin shift →
        // mueven el cursor (y resetean el ancla de selección).
        if (k === '◀') {
            if (shift) extenderSeleccion(textarea, -1, anclaSeleccion);
            else {
                moverCursor(textarea, -1);
                anclaSeleccion.valor = null;
            }
            guardarCaret();
            return;
        }
        if (k === '▶') {
            if (shift) extenderSeleccion(textarea, 1, anclaSeleccion);
            else {
                moverCursor(textarea, 1);
                anclaSeleccion.valor = null;
            }
            guardarCaret();
            return;
        }
        if (k === 'espacio') {
            escribirEnCursor(textarea, ' ');
            cambio();
            return;
        }
        // Tecla normal. Reseteamos el ancla: escribir reemplaza la selección y
        // termina cualquier selección en curso.
        escribirEnCursor(textarea, shift ? k.toUpperCase() : k);
        anclaSeleccion.valor = null;
        if (shift) {
            shift = false;
            render();
        }
        cambio();
    }

    // ── MOTOR DE EVENTOS ──────────────────────────────────────────────────
    // CRÍTICO: actuamos en `pointerdown` (con preventDefault) — NO en `click`.
    //  · preventDefault en pointerdown impide que el textarea pierda foco/caret.
    //  · iOS Safari CANCELA el `click` sintético cuando se previene el default del
    //    pointer/touch → si dependiéramos de `click`, las teclas NO dispararían.
    //    Ese era exactamente el bug "no dispara".
    //  · passive:false es OBLIGATORIO o preventDefault() se ignora en móvil.
    //
    // El long-press de acentos arma un timer; si se cumple, el pointerup NO escribe
    // la letra base (solo abre el popover). El popover de acentos se confirma con su
    // propio pointerdown.

    let timerLongPress = null;
    let teclaPendiente = null; // botón [data-k] presionado, a resolver en pointerup

    el.addEventListener(
        'pointerdown',
        e => {
            const btn = e.target.closest('.pm-tec, .pm-tec-emoji, [data-acento]');
            if (!btn) return;
            // Mantener foco+caret en el textarea: este es el preventDefault quirúrgico.
            e.preventDefault();
            // Re-asegurar foco por si iOS lo soltó (scroll/rotación previa).
            restaurarCaret();

            // Acento del popover: confirmar al instante (no espera pointerup, así el
            // tap sobre el popover es inmediato y no se confunde con el long-press).
            if (btn.dataset.acento != null) {
                ejecutarTecla(btn);
                teclaPendiente = null;
                return;
            }
            // Emoji del panel.
            if (btn.classList.contains('pm-tec-emoji')) {
                escribirEnCursor(textarea, btn.dataset.emoji);
                cambio();
                teclaPendiente = null;
                return;
            }

            teclaPendiente = btn;

            // Long-press → popover de acentos.
            if (btn.dataset.acentos != null) {
                const base = btn.dataset.k;
                timerLongPress = setTimeout(() => {
                    teclaPendiente = null; // ya NO escribir la base al soltar
                    abrirPopoverAcentos(btn, base);
                }, MS_LONG_PRESS);
            }
        },
        { passive: false }
    );

    // pointerup: ejecuta la tecla pendiente (tap corto). Si hubo long-press de
    // acentos, teclaPendiente ya es null → no escribe la base.
    el.addEventListener('pointerup', () => {
        clearTimeout(timerLongPress);
        if (teclaPendiente) {
            ejecutarTecla(teclaPendiente);
            teclaPendiente = null;
        }
    });
    // pointercancel = el SO abortó el gesto (scroll, multitouch, llamada entrante):
    // cancelar de verdad. NO usamos pointerleave para cancelar: con la captura
    // implícita del puntero en touch, leave puede dispararse a destiempo y "comerse"
    // taps legítimos (reintroduciría el "no dispara" de forma intermitente).
    el.addEventListener('pointercancel', () => {
        clearTimeout(timerLongPress);
        teclaPendiente = null;
    });

    // Belt-and-suspenders para ratón legacy: en navegadores que aún emiten mousedown
    // junto al pointer, prevenir su default también evita el blur del textarea. La
    // EJECUCIÓN de la tecla la hace pointerdown/pointerup (arriba); esto sólo cuida
    // el foco. Pointer Events está en todos los navegadores modernos del panel.
    el.addEventListener('mousedown', e => {
        if (e.target.closest('.pm-tec, .pm-tec-emoji, [data-acento]')) e.preventDefault();
    });

    function abrirPopoverAcentos(btn, base) {
        cerrarAcentos();
        const acentos = ACENTOS[base] || [];
        popoverAcentos = document.createElement('div');
        popoverAcentos.className = 'pm-tec-acentos';
        popoverAcentos.innerHTML = acentos
            .map(a => {
                const car = shift ? a.toUpperCase() : a;
                return `<button type="button" class="pm-tec" data-acento="${car}">${car}</button>`;
            })
            .join('');
        const rect = btn.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        popoverAcentos.style.left = rect.left - elRect.left + 'px';
        popoverAcentos.style.top = rect.top - elRect.top - 48 + 'px';
        el.appendChild(popoverAcentos);
    }

    // Panel de emojis (toggle). Vive como hijo de `el`, sobrevive a render() de filas.
    function toggleEmojis() {
        if (panelEmoji) {
            panelEmoji.remove();
            panelEmoji = null;
            return;
        }
        panelEmoji = document.createElement('div');
        panelEmoji.className = 'pm-tec-emojis';
        panelEmoji.innerHTML = EMOJIS.map(
            em => `<button type="button" class="pm-tec-emoji" data-emoji="${em}">${em}</button>`
        ).join('');
        // Los emojis se atienden en el pointerdown global de `el` (data-emoji) → no
        // necesita listener propio; así heredan el preventDefault de foco.
        el.insertBefore(panelEmoji, el.firstChild);
    }

    // Doble-tap sobre el textarea = seleccionar la palabra (como cualquier editor).
    // Rápido para "poner esta palabra en negrita". Detecta 2 taps < 350ms cercanos.
    let ultimoTap = 0;
    function onDobleTextarea() {
        // El navegador ya posicionó el caret en el tap; seleccionamos la palabra ahí.
        if (seleccionarPalabra(textarea, textarea.selectionStart)) {
            anclaSeleccion.valor = null; // selección por palabra: ancla limpia
            guardarCaret();
            if (onChange) onChange();
        }
    }
    function onTapTextarea() {
        const ahora = performance.now ? performance.now() : 0;
        if (ahora - ultimoTap < 350) onDobleTextarea();
        ultimoTap = ahora;
    }
    textarea.addEventListener('click', onTapTextarea);

    render();
    actualizarBotonesUndo();
    return {
        el,
        destroy() {
            clearTimeout(timerLongPress);
            cerrarAcentos();
            textarea.removeEventListener('click', onTapTextarea);
            if (panelEmoji) {
                panelEmoji.remove();
                panelEmoji = null;
            }
        },
    };
}

// Íconos de deshacer/rehacer (Material Design undo/redo): flecha-vuelta con la
// punta bien dibujada (fill, no stroke — se ve nítida a 20px). Undo apunta a la
// izquierda, redo es el espejo.
function iconUndo() {
    return '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/></svg>';
}
function iconRedo() {
    return '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z"/></svg>';
}
